import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { Button } from '@agms/ui'
import { ticketApi } from '../../lib/api'
import type { TicketStatus } from '../../lib/api'
import { apiErrorMessage } from '../../hooks/useErrorHandler'

interface DraftHelpArticleButtonProps {
  ticketId: string
  status: TicketStatus
  canCreate: boolean
  configured: boolean
}

export function DraftHelpArticleButton({ ticketId, status, canCreate, configured }: DraftHelpArticleButtonProps) {
  const [error, setError] = useState<string | null>(null)
  const [slug, setSlug] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: () => ticketApi.draftHelpArticle(ticketId),
    onSuccess: (result) => {
      setError(null)
      setSlug(result.article.slug)
    },
    onError: (caught) => {
      setSlug(null)
      setError(apiErrorMessage(caught, 'The article draft could not be created. Try again.'))
    },
  })

  if (status !== 'resolved' || !canCreate) {
    return null
  }

  if (!configured) {
    return <p className="text-sm text-gray-600">Article drafting is not configured.</p>
  }

  return (
    <div className="space-y-2">
      <Button onClick={() => mutation.mutate()} disabled={mutation.isPending || slug !== null}>
        {mutation.isPending ? 'Drafting...' : 'Draft article from this ticket'}
      </Button>
      {error ? (
        <p className="text-sm text-rose-700" role="alert">{error}</p>
      ) : null}
      {slug ? (
        <Link to={`/help/${slug}`} className="block text-sm font-medium text-blue-700 hover:text-blue-900">
          Review the draft article
        </Link>
      ) : null}
    </div>
  )
}
